import * as React from 'react';
import {defaults} from 'lodash';
// @ts-expect-error - TS7016 - Could not find a declaration file for module 'react-recompose'. '/home/runner/work/flow-to-typescript-codemod/flow-to-typescript-codemod/webflow/node_modules/react-recompose/dist/Recompose.cjs.js' implicitly has an 'any' type.
import {withState} from 'react-recompose';
import {controlReset, unselectable} from './constants/Styles';
import {propsToIgnorePasswordManagers} from '@packages/systems/spring';
import {emotionStyled, styled} from '@packages/utilities/styled';
import {weakMemo, memoize} from '@packages/utilities/memo';

import type {CSSProperties} from '@packages/utilities/css-properties-type';

const BaseInputView = emotionStyled('input')({
  boxSizing: 'border-box',
  borderStyle: 'solid',
  '&::placeholder': {
    color: '$textDimmed',
  },
});

const styledInput = styled(BaseInputView);

// @ts-expect-error - TS7031 - Binding element 'gutters' implicitly has an 'any' type. | TS7031 - Binding element 'control' implicitly has an 'any' type. | TS7031 - Binding element 'TextInput' implicitly has an 'any' type. | TS7031 - Binding element 'style' implicitly has an 'any' type.
const inputStyle = ({theme: {gutters, control, TextInput}, style}) =>
  defaults(
    {
      paddingRight: gutters.base,
      paddingLeft: gutters.base,
      borderWidth: control.borderWidth,
      borderColor: TextInput.borderColor,
      borderRadius: control.borderRadius,
      lineHeight: control.lineHeight,
      background: TextInput.background,
      boxShadow: TextInput.boxShadow,
      color: TextInput.color,
      ...style,
    },
    controlReset
  );

const getDarkVariant = ({
  // @ts-expect-error - TS7031 - Binding element 'colors' implicitly has an 'any' type. | TS7031 - Binding element 'TextInputDark' implicitly has an 'any' type.
  theme: {colors, TextInputDark},
  // @ts-expect-error - TS7031 - Binding element 'focus' implicitly has an 'any' type.
  focus,
  // @ts-expect-error - TS7031 - Binding element 'disabled' implicitly has an 'any' type.
  disabled,
  // @ts-expect-error - TS7031 - Binding element 'hover' implicitly has an 'any' type.
  hover,
}) => {
  if (focus || hover) {
    return {background: TextInputDark.focus.background};
  }
  if (disabled) {
    return {background: TextInputDark.disabled.background};
  }
  return {background: colors.backgroundDark};
};

const variations = {
  // @ts-expect-error - TS7031 - Binding element 'colors' implicitly has an 'any' type. | TS7031 - Binding element 'focusColors' implicitly has an 'any' type. | TS7031 - Binding element 'TextInput' implicitly has an 'any' type. | TS7031 - Binding element 'style' implicitly has an 'any' type.
  focus: ({theme: {colors, TextInput}, style}) => {
    const styles = {
      background: TextInput.focus.background,
      ...style,
    } as const;

    styles.borderColor = colors.accent;

    return styles;
  },

  // @ts-expect-error - TS7031 - Binding element 'focusColors' implicitly has an 'any' type. | TS7031 - Binding element 'TextInput' implicitly has an 'any' type.
  invalid: ({theme: {focusColors, TextInput}}) => ({
    color: focusColors.errorTextColor,
    boxShadow: [TextInput.boxShadow, focusColors.errorShadow].join(','),
    borderColor: 'transparent',
  }),

  // @ts-expect-error - TS7031 - Binding element 'focusColors' implicitly has an 'any' type. | TS7031 - Binding element 'TextInput' implicitly has an 'any' type.
  warning: ({theme: {focusColors, TextInput}}) => ({
    color: focusColors.warningTextColor,
    boxShadow: [TextInput.boxShadow, focusColors.warningShadow].join(','),
    borderColor: 'transparent',
  }),

  // @ts-expect-error - TS7031 - Binding element 'colors' implicitly has an 'any' type. | TS7031 - Binding element 'TextInput' implicitly has an 'any' type.
  disabled: ({theme: {colors, TextInput}}) => ({
    color: TextInput.disabled.color,
    background: TextInput.disabled.background,
    borderColor: colors.border,
    boxShadow: 'none',
    ...unselectable,
  }),

  // @ts-expect-error - TS7031 - Binding element 'TextInput' implicitly has an 'any' type. | TS7031 - Binding element 'colors' implicitly has an 'any' type. | TS7031 - Binding element 'disabled' implicitly has an 'any' type. | TS7031 - Binding element 'focus' implicitly has an 'any' type.
  hover: ({theme: {TextInput, colors}, disabled, focus, style}) => {
    if (disabled) {
      return {
        background: TextInput.disabled.background,
      };
    }

    const styles: Record<string, any> = {};
    styles.background = TextInput.focus.background;

    if (focus) {
      styles.borderColor = colors.accent;
    } else {
      styles.boxShadow = TextInput.boxShadow;
    }

    return {...styles, ...style};
  },
  dark: getDarkVariant,
} as const;

const inputState = withState('selectAll', 'setState', true);

// @ts-expect-error - TS7031 - Binding element 'selectAll' implicitly has an 'any' type. | TS7031 - Binding element 'setState' implicitly has an 'any' type.
const AutoFocusInputState = inputState(({selectAll, setState, ...props}) => {
  const onFocus = (e: any) => {
    props.onFocus && props.onFocus();

    if (selectAll) {
      e.target.select();
      setState(false);
    }
  };

  // TODO: fix jsx-a11y lint error
  // eslint-disable-next-line jsx-a11y/no-autofocus
  return <input autoComplete="off" {...props} autoFocus onFocus={onFocus} />;
});

/**
 * @deprecated use `TextInput` from `@packages/systems/spring` instead
 */
export const AutoFocusInput = styled(AutoFocusInputState)(
  inputStyle,
  variations
);

type SharedTextInputProps = {
  value?: number | string;
  style?: CSSProperties;
  placeholder?: string;
  focus?: boolean;
  invalid?: boolean;
  disabled?: boolean;
  hover?: boolean;
  autoFocus?: boolean;
  onFocus?: (arg1: React.FocusEvent<HTMLInputElement>) => void;
  onChange?: (arg1: React.ChangeEvent<HTMLInputElement>) => void;
  onBlur?: (arg1: React.FocusEvent<HTMLInputElement>) => void;
  maxLength?: number;
  onKeyPress?: (arg1: React.KeyboardEvent<HTMLInputElement>) => void;
  onKeyDown?: (arg1: React.KeyboardEvent<HTMLInputElement>) => void;
  onKeyUp?: (arg1: React.KeyboardEvent<HTMLInputElement>) => void;
  ['aria-label']?: string;
  ['data-automation-id']?: string;
  defaultValue?: string | number;
  tabIndex?: number;
  id?: string;
  name?: string;
  type?: string;
  onDragStart?: (arg1: React.MouseEvent<HTMLInputElement>) => void;
  onDrop?: (arg1: React.MouseEvent<HTMLInputElement>) => void;
  onMouseEnter?: (event?: React.MouseEvent<HTMLInputElement>) => void;
  onMouseOver?: (event?: React.MouseEvent<HTMLInputElement>) => void;
  onMouseLeave?: (event?: React.MouseEvent<HTMLInputElement>) => void;
  onMouseDown?: (event?: React.MouseEvent<HTMLInputElement>) => void;
  onMouseUp?: (event?: React.MouseEvent<HTMLInputElement>) => void;
  /**
   * Ignores password managers from suggesting passwords.
   */
  shouldIgnorePasswordManagers?: boolean;
};

type StyledInputProps = SharedTextInputProps & {
  ['data-wf-base-text-input']?: boolean;
  autoComplete?: string;
};

const StyledInput: React.ComponentType<StyledInputProps> = styledInput(
  // @ts-expect-error - TS2345 - Argument of type '({ theme: { gutters, control, TextInput }, style, ...props }: { theme: { gutters: any; control: any; TextInput: any; }; style: any; } & StyledInputProps) => any' is not assignable to parameter of type 'StyleObject'.
  inputStyle,
  variations
);

type TextInputProps = SharedTextInputProps & {
  selectAll?: boolean;
};

// @ts-expect-error - TS7006 - Parameter 'onFocus' implicitly has an 'any' type.
const withSelectAllFocusHandler = weakMemo((onFocus) =>
  // @ts-expect-error - TS7006 - Parameter 'selectAll' implicitly has an 'any' type.
  memoize((selectAll) => (event: React.FocusEvent<HTMLInputElement>) => {
    onFocus(event);
    if (selectAll) {
      event.currentTarget.select();
    }
  })
);

/**
 * @deprecated use `TextInput` from `@packages/systems/spring` instead
 */
const TextInput: React.ComponentType<TextInputProps> = React.forwardRef(
  (
    {selectAll, onFocus, shouldIgnorePasswordManagers = true, ...props},
    ref
  ) => {
    return (
      <StyledInput
        data-wf-base-text-input
        data-legacy-ds="TextInput"
        autoComplete="off"
        onFocus={onFocus && withSelectAllFocusHandler(onFocus)(selectAll)}
        // @ts-expect-error - TS2322 - Type '{ value?: string | number | undefined; style?: ObjMap<StrictCSSProperties, ToMaybeType> | undefined; placeholder?: string | undefined; ... 28 more ...; ref: ForwardedRef<...>; }' is not assignable to type 'IntrinsicAttributes & SharedTextInputProps & { "data-wf-base-text-input"?: boolean | undefined; autoComplete?: string | undefined; }'.
        ref={ref}
        {...(shouldIgnorePasswordManagers && propsToIgnorePasswordManagers)}
        {...props}
      />
    );
  }
);

export default TextInput;
